import type { FeatureKey, SpectralAnalysisSnapshot } from '../types';
import { PSD_SAMPLE_POINTS } from './constants';
import type { VerificationHrDataSource } from './resolveVerificationSnapshot';

const FEATURE_KEYS: FeatureKey[] = ['HR', 'HRV', 'LF', 'HF'];

const sampleSeries = (values: Array<number | undefined>, count: number) => {
  if (values.length <= count * 2) return values;
  return {
    head: values.slice(0, count),
    tail: values.slice(-count),
    length: values.length,
  };
};

const samplePsd = (frequencies: number[], psd: number[]) => ({
  sample: frequencies.slice(0, PSD_SAMPLE_POINTS).map((frequency, index) => ({
    frequency,
    power: psd[index] ?? 0,
  })),
  length: frequencies.length,
});

export const buildVerificationEvidence = (
  snapshot: SpectralAnalysisSnapshot,
  dataSourcesBySessionId: Record<number, VerificationHrDataSource> = {}
) => ({
  analysis: {
    analysisId: snapshot.analysisId,
    name: snapshot.name,
    author: snapshot.author,
    createdAt: snapshot.createdAt,
    selectedCount: snapshot.selectedSessions.length,
    includedCount: snapshot.includedSessions.length,
    excludedCount: snapshot.excludedSessions.length,
    dataSourcePolicy:
      'Use hrData_repaired when available with effective repair markers, otherwise hrData',
    dataSourcesBySessionId,
  },
  groupSummary: {
    totalSessions: snapshot.groupSummary.totalSessions,
    includedSessions: snapshot.groupSummary.includedSessions,
    excludedSessions: snapshot.groupSummary.excludedSessions,
    outlierCount: snapshot.groupSummary.outlierCount,
    averageHrDelta: snapshot.groupSummary.averageHrDelta,
    averageLfHfDelta: snapshot.groupSummary.averageLfHfDelta,
  },
  excludedSessions: snapshot.excludedSessions.map(item => ({
    sessionId: item.sessionId,
    reason: item.reason,
    validDataPercentByFeature: item.validDataPercentByFeature,
  })),
  includedSessions: snapshot.includedSessions.map(item => ({
    sessionId: item.validation.sessionId,
    patientId: item.validation.patientId,
    hrDataSource: dataSourcesBySessionId[item.validation.sessionId] ?? 'hrData',
    quality: {
      level: item.validation.quality.level,
      score: item.validation.quality.score,
      validDataPercentByFeature:
        item.validation.quality.validDataPercentByFeature,
      gaps: item.validation.quality.gaps.length,
      interpolatedGaps: item.validation.quality.gaps.filter(
        gap => gap.interpolated
      ).length,
      severeGaps: item.validation.quality.gaps.filter(gap => !gap.interpolated)
        .length,
      duplicatesRemoved: item.validation.quality.duplicatesRemoved,
    },
    segments: {
      baseline: item.validation.segments.baseline,
      exposure: item.validation.segments.exposure,
    },
    preprocessingCounts: item.validation.preprocessingCounts,
    preprocessingSummary: item.spectral.preprocessingSummary,
    cleaningLogSample: item.validation.cleaningLog.slice(0, 8),
    temporalMetrics: FEATURE_KEYS.reduce(
      (accumulator, feature) => ({
        ...accumulator,
        [feature]: {
          baselineMean: item.validation.metrics[feature].baseline.mean,
          exposureMean: item.validation.metrics[feature].exposure.mean,
          delta: item.validation.metrics[feature].delta,
        },
      }),
      {} as Record<
        FeatureKey,
        { baselineMean: number; exposureMean: number; delta: number }
      >
    ),
    spectralMetrics: {
      LF: {
        baselinePower: item.spectral.spectralMetrics.LF.baseline.power,
        exposurePower: item.spectral.spectralMetrics.LF.exposure.power,
        delta: item.spectral.spectralMetrics.LF.delta,
      },
      HF: {
        baselinePower: item.spectral.spectralMetrics.HF.baseline.power,
        exposurePower: item.spectral.spectralMetrics.HF.exposure.power,
        delta: item.spectral.spectralMetrics.HF.delta,
      },
      LFHF_ratio: item.spectral.spectralMetrics.LFHF_ratio,
    },
    psd: {
      baseline: {
        lf: samplePsd(
          item.spectral.psd.baseline.frequencies,
          item.spectral.psd.baseline.psd_lf
        ),
        hf: samplePsd(
          item.spectral.psd.baseline.frequencies,
          item.spectral.psd.baseline.psd_hf
        ),
      },
      exposure: {
        lf: samplePsd(
          item.spectral.psd.exposure.frequencies,
          item.spectral.psd.exposure.psd_lf
        ),
        hf: samplePsd(
          item.spectral.psd.exposure.frequencies,
          item.spectral.psd.exposure.psd_hf
        ),
      },
    },
    seriesSample: {
      baseline: FEATURE_KEYS.reduce(
        (accumulator, feature) => ({
          ...accumulator,
          [feature]: sampleSeries(
            item.validation.series.baseline.values[feature],
            PSD_SAMPLE_POINTS
          ),
        }),
        {} as Record<FeatureKey, unknown>
      ),
      exposure: FEATURE_KEYS.reduce(
        (accumulator, feature) => ({
          ...accumulator,
          [feature]: sampleSeries(
            item.validation.series.exposure.values[feature],
            PSD_SAMPLE_POINTS
          ),
        }),
        {} as Record<FeatureKey, unknown>
      ),
    },
    outlier: item.spectral.outlier,
  })),
});

export type VerificationEvidence = ReturnType<typeof buildVerificationEvidence>;
